import { parseZonedDateTime } from '@internationalized/date';
import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import { LoadSortOption, LoadStatus } from '@rematter/frontend-lib-api-client';

import { LoadLineItemRecord } from '../types/load';
import { OutboundTransactionRecord } from '../types/transaction';
import { getLoadLineItems } from '../utils/loadLineItems';
import { derivePaymentStatus } from '../utils/paymentStatus';
import {
  calculateLineItemsTotal,
  calculateTotalNetWeightFromLoadItems,
} from '../utils/weight';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const transactionsOutboundQueryAtom = createQueryAtomFactory(
  'trade.transactionsOutbound',
  (params: {
    searchTerm?: string;
    facilityId?: string;
    facilityIds?: string[];
    accountIds?: string[];
    status?: LoadStatus[];
    columnStatuses?: LoadStatus[];
    fromShippedDate?: string;
    toShippedDate?: string;
    limit?: number;
    offset?: number;
    sorting?: LoadSortOption[];
  }) => ({
    loads: {
      __args: {
        filter: {
          status: params.columnStatuses || params.status,
          searchTerm: params.searchTerm || undefined,
          accountIds: params.accountIds,
          facilityId: params.facilityId,
          facilityIds: params.facilityIds,
          fromShippedDate: params.fromShippedDate,
          toShippedDate: params.toShippedDate,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      page: {
        total: true,
      },
      entries: {
        id: true,
        status: true,
        shippedDate: true,
        documentNumber: true,
        salesOrderId: true,
        receivingTicketNumber: true,
        customerExternalNotes: true,
        purchaseOrderId: true,
        purchaseOrder: {
          documentNumber: true,
        },
        salesOrder: {
          documentNumber: true,
          account: {
            name: true,
            type: true,
          },
        },
        reconciliation: {
          invoiceId: true,
          invoice: {
            documentNumber: true,
            status: true,
            amount: {
              amount: true,
            },
            openAmount: {
              amount: true,
              currencyCode: true,
            },
          },
        },
        grandTotal: {
          amount: true,
          currencyCode: true,
        },
        lineItems: {
          id: true,
          purchaseOrder: {
            documentNumber: true,
          },
          unitPrice: {
            amount: true,
            currencyCode: true,
          },
          pricingUnit: true,
          phase: true,
          weights: true,
        },
        facilityId: true,
        facility: {
          id: true,
          facilityName: true,
        },
        timestamps: {
          createdAt: true,
        },
      },
    },
  }),
  {
    select: ({
      loads,
    }): {
      total: number;
      loads: OutboundTransactionRecord[];
    } => ({
      loads: loads?.entries?.map((load) => {
        const loadPONumber = load.purchaseOrder?.documentNumber;
        const lineItemPONumbers = new Set(
          load.lineItems
            .map((li) => li.purchaseOrder?.documentNumber)
            .filter(
              (n: string | undefined): n is string =>
                n != null && n !== loadPONumber,
            ),
        );

        const { relevantLineItems, shippedLineItems } = getLoadLineItems(
          load.lineItems as unknown as LoadLineItemRecord[],
        );

        const totalNetWeight = calculateTotalNetWeightFromLoadItems(
          relevantLineItems as LoadLineItemRecord[],
        );

        const shippedTotal = calculateLineItemsTotal(shippedLineItems);

        return {
          id: load.id,
          loadNumber: load.documentNumber,
          status: load.status,
          shippedDate: maybeParseCalendarDate(load.shippedDate),
          salesOrderId: load.salesOrderId ?? undefined,
          salesOrderNumber: load.salesOrder?.documentNumber ?? undefined,
          purchaseOrderNumber: loadPONumber ?? undefined,
          additionalPOCount: lineItemPONumbers.size,
          receivingTicketNumber: load.receivingTicketNumber ?? undefined,
          invoiceId: load.reconciliation?.invoiceId ?? undefined,
          invoiceNumber:
            load.reconciliation?.invoice?.documentNumber ?? undefined,
          paymentStatus: derivePaymentStatus(load.reconciliation?.invoice),
          customer: load.salesOrder?.account?.name ?? undefined,
          customerType: load.salesOrder?.account?.type ?? undefined,
          facilityId: load.facilityId,
          facility: load.facility,
          lineItemCount: load.lineItems.length,
          totalNetWeight,
          shippedTotal,
          grandTotal: load.grandTotal ?? null,
          outstandingBalance: load.reconciliation?.invoice?.openAmount ?? null,
          customerExternalNotes: load.customerExternalNotes ?? undefined,
          timestamps: {
            createdAt: parseZonedDateTime(load.timestamps.createdAt),
          },
        };
      }),
      total: loads.page.total ?? 0,
    }),
  },
);
